import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { ResidentDialogShell } from '@/features/resident/components/dialogs/ResidentDialogShell'
import { updatePmsResidentMatchStatus } from '@/features/resident/services/yardiResidentService'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import modalStyles from '@/features/application/styles/modals.module.css'
import type { MatchingResidentRow } from '../types'

const REASONS = [
  { id: 'HISTORICAL', label: 'Historical' },
  { id: 'NO_PMS_EQUIVALENT', label: 'No PMS equivalent' },
  { id: 'INSUFFICIENT_DATA', label: 'Insufficient data' },
  { id: 'INVALID_PMS_RECORD', label: 'Invalid PMS record' },
  { id: 'DUPLICATE_PMS_RECORD', label: 'Duplicate PMS record' },
  { id: 'BUSINESS_DECISION', label: 'Business decision not to map' },
]

export function UnmatchableDialog({
  row,
  onClose,
  onSaved,
}: {
  row: MatchingResidentRow | null
  onClose: () => void
  onSaved: () => void
}) {
  const [reason, setReason] = useState(row?.unmatchable_reason || 'HISTORICAL')
  const [note, setNote] = useState(row?.admin_note || '')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setReason(row?.unmatchable_reason || 'HISTORICAL')
    setNote(row?.admin_note || '')
  }, [row])

  const save = async (status: 'UNMATCHABLE' | 'PENDING') => {
    if (!row) return
    setSaving(true)
    try {
      await updatePmsResidentMatchStatus({
        property_id: row.liberty_property_id,
        company_id: row.liberty_company_id,
        property_code: row.source_property_id,
        resident_default_id: row.liberty_entity_id,
        status,
        reason: status === 'UNMATCHABLE' ? reason : undefined,
        note: note.trim() || undefined,
      })
      showSuccess(status === 'PENDING' ? 'Resident reset to pending.' : 'Resident marked unmatchable.')
      onSaved()
      onClose()
    } catch (error) {
      showApiError(error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <ResidentDialogShell
      isOpen={!!row}
      title="Update resident matching status"
      onClose={onClose}
      maxWidth="max-w-xl"
      footerAlign="end"
      footer={
        <div className="grid w-full grid-cols-3 gap-2">
          <button
            type="button"
            className={`${modalStyles.btnSaveChangesSecondary} !flex !h-11 !w-full !min-w-0 items-center justify-center whitespace-nowrap px-2 text-sm`}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`${modalStyles.btnSaveChangesSecondary} !flex !h-11 !w-full !min-w-0 items-center justify-center whitespace-nowrap px-2 text-sm`}
            disabled={saving}
            onClick={() => void save('PENDING')}
          >
            Reset to pending
          </button>
          <button
            type="button"
            className={`${modalStyles.btnSaveChangesPrimary} !flex !h-11 !w-full !min-w-0 items-center justify-center gap-1 whitespace-nowrap px-2 text-sm`}
            disabled={saving}
            onClick={() => void save('UNMATCHABLE')}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Mark unmatchable
          </button>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-slate-600">
          {row ? `${row.first_name || ''} ${row.last_name || ''}`.trim() || `Resident ${row.liberty_entity_id}` : ''}
        </p>
        <label className="block font-medium text-slate-700">
          Reason
          <select
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3"
          >
            {REASONS.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block font-medium text-slate-700">
          Note
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            className="mt-1 min-h-[80px] w-full rounded-md border border-slate-300 px-3 py-2"
          />
        </label>
      </div>
    </ResidentDialogShell>
  )
}
